import type PubNub from 'pubnub'
import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects'

import {
  generateChannelID,
  getChannelUsersMetadata,
  getMembershipToChannel,
  getPubNubInstance,
  getUuid,
  setMembershipToChannel,
  setUserMetadata,
} from 'src/helpers/PubNubHelpers'
import { User } from 'src/rest/types/User'
import { actions, selectors } from '..'

export default class ChatSagas {
  static *init() {
    const user: User = yield select(selectors.auth.user)
    const pubnub: PubNub = yield call(getPubNubInstance, getUuid(user))

    if (pubnub && user) {
      const memberships: PubNub.ManageMembershipsResponse<
        PubNub.ObjectCustom,
        PubNub.ObjectCustom
      > | null = yield call(getMembershipToChannel, pubnub, getUuid(user))

      if (memberships) {
        yield put(actions.chat.setMemberships(memberships.data))
        yield call(setUserMetadata, pubnub, user)
      }
    }
  }

  static *loadChannelUsers({
    payload: channel,
  }: ReturnType<typeof actions.chat.setCurrentChannel>) {
    if (!channel) {
      return
    }

    const user: User = yield select(selectors.auth.user)
    const pubnub: PubNub = yield call(getPubNubInstance, getUuid(user))
    const usersMetadata:
      | PubNub.UUIDMetadataObject<PubNub.ObjectCustom>[]
      | null = yield call(getChannelUsersMetadata, pubnub, channel)

    if (usersMetadata) {
      yield put(actions.chat.setUsersMetadata(usersMetadata))
    }
  }

  static *createNewChannel({
    payload,
  }: ReturnType<typeof actions.chat.createNewChannel>) {
    const user: User = yield select(selectors.auth.user)
    const pubnub: PubNub = yield call(getPubNubInstance, getUuid(user))
    const otherUser = payload.classifiedAuthor.user

    if (otherUser === undefined) {
      throw new Error('Can’t create new channel, there is no other user')
    }

    const channelID = generateChannelID(user.id, otherUser.id, payload.response)

    yield call(setMembershipToChannel, pubnub, getUuid(otherUser), channelID) // subscribe selected user to the new channel
    yield call(setMembershipToChannel, pubnub, getUuid(user), channelID) // subscribe current user to the new channel

    yield put(actions.chat.setCurrentChannel(channelID))
    yield put(actions.chat.sendFirstMessage(payload))

    // Force refresh unread count for current user
    yield call(setMembershipToChannel, pubnub, getUuid(user), channelID)
  }

  static *sendFirstMessage({
    payload,
  }: ReturnType<typeof actions.chat.sendFirstMessage>) {
    const user: User = yield select(selectors.auth.user)
    const pubnub: PubNub = yield call(getPubNubInstance, getUuid(user))
    const currentChannel: string = yield select(selectors.chat.currentChannel)

    pubnub.publish({
      message: { text: payload.message },
      channel: currentChannel,
    })
  }

  static *refreshUnreadCount() {
    const user: User = yield select(selectors.auth.user)
    const uuid = getUuid(user)
    const pubnub = getPubNubInstance(uuid)

    if (uuid && pubnub) {
      const userMembership: PubNub.ManageMembershipsResponse<
        PubNub.ObjectCustom,
        PubNub.ObjectCustom
      > | null = yield call(getMembershipToChannel, pubnub, uuid)

      if (!userMembership) {
        return
      }

      const channels: string[] = []
      const channelTimetokens: string[] = []

      if (userMembership?.data) {
        userMembership.data.forEach((entry: any) => {
          if (entry?.channel?.id && entry?.custom?.lastReadTimetoken) {
            channels.push(entry?.channel?.id)
            channelTimetokens.push(entry?.custom?.lastReadTimetoken)
          }
        })

        if (channels.length > 0 && pubnub) {
          try {
            const data: PubNub.MessageCountsResponse = yield call(
              pubnub.messageCounts,
              {
                channels,
                channelTimetokens,
              }
            )
            if (data?.channels) {
              yield put(actions.chat.setUnreadMessagesCount(data.channels))
            }
          } catch (error) {
            console.log(error)
          }
        }
      }
    }
  }

  static *loop() {
    yield all([
      //
      takeLeading(actions.chat.init, this.init),
      takeLatest(actions.chat.setCurrentChannel, this.loadChannelUsers),
      takeLeading(actions.chat.createNewChannel, this.createNewChannel),
      takeLeading(actions.chat.sendFirstMessage, this.sendFirstMessage),
      takeLeading(actions.chat.refreshUnreadCount, this.refreshUnreadCount),
    ])
  }
}
